import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Star, KeyRound, Check, Eye, EyeOff, Mail, ExternalLink, Sparkles, Heart, Copy, Loader2, Image as ImageIcon, RefreshCw, Languages, Globe, Users } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi, mediaUrl } from "./adminApi";
import { fmtPrice } from "../api";

const STATUS_COLORS = {
  "Disponible": "bg-emerald-500/20 text-emerald-300",
  "Sous compromis": "bg-amber-500/20 text-amber-300",
  "Vendu": "bg-red-500/20 text-red-300",
};

function GlobalPasswordBar() {
  const [pw, setPw] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => { adminApi.getSettings().then((d) => setPw(d.global_listing_password || "")); }, []);
  const save = async () => { await adminApi.updateSettings({ global_listing_password: pw }); setSaved(true); setTimeout(() => setSaved(false), 2000); };
  return (
    <div className="bg-[#111114] border border-white/10 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="global-password-bar">
      <div className="flex items-center gap-2 text-sm text-zinc-300 shrink-0">
        <KeyRound className="w-4 h-4 text-[#C19A6B]" /> Mot de passe global des fiches
      </div>
      <input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="global-password-input" className="flex-1 bg-white/5 border border-white/10 px-4 py-2 text-sm text-white focus:outline-none focus:border-[#C19A6B]" />
      <button onClick={save} data-testid="global-password-save" className="flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors">
        {saved ? <><Check className="w-4 h-4" /> Enregistré</> : "Enregistrer"}
      </button>
    </div>
  );
}

export default function Dashboard({ embedded }) {
  const [items, setItems] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [aiFilter, setAiFilter] = useState(false);
  const [members, setMembers] = useState([]);

  const load = () => adminApi.list().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); adminApi.listMembers("approved").then(setMembers).catch(() => setMembers([])); }, []);

  const reassignOwner = async (l, ownerId) => {
    await adminApi.update(l.slug, { owner_id: ownerId });
    load();
  };

  const isAi = (l) => !!(l.ai_content && l.ai_content.intro);
  const aiCount = (items || []).filter(isAi).length;
  const shown = aiFilter ? (items || []).filter(isAi) : (items || []);

  const del = async (slug) => {
    await adminApi.remove(slug);
    setConfirm(null);
    load();
  };

  const togglePublish = async (l) => {
    await adminApi.update(l.slug, { published: l.published === false });
    load();
  };

  const [dupBusy, setDupBusy] = useState(null);
  const duplicate = async (l) => {
    setDupBusy(l.slug);
    try { await adminApi.duplicate(l.slug); load(); }
    catch { /* noop */ }
    finally { setDupBusy(null); }
  };

  const [hlBatch, setHlBatch] = useState({ busy: false, msg: "" });
  const missingHlCount = (items || []).filter((l) => !(Array.isArray(l.highlights) && l.highlights.filter(Boolean).length)).length;
  const runBatchHighlights = async (onlyMissing) => {
    setHlBatch({ busy: true, msg: "" });
    try {
      const r = await adminApi.generateHighlightsBatch({ only_missing: onlyMissing });
      await load();
      setHlBatch({ busy: false, msg: `${r.updated} annonce(s) mise(s) à jour sur ${r.processed} traitée(s).` });
      setTimeout(() => setHlBatch((s) => ({ ...s, msg: "" })), 6000);
    } catch (e) {
      setHlBatch({ busy: false, msg: "Erreur lors de la génération en lot." });
    }
  };

  const [mediaBatch, setMediaBatch] = useState({ busy: false, msg: "", done: 0, total: 0 });
  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const s = await adminApi.mediaBatchStatus();
        if (s && s.running) {
          setMediaBatch({ busy: true, msg: "", done: s.done || 0, total: s.total || 0 });
          timer = setTimeout(poll, 3000);
        } else if (s && (s.total || s.updated != null) && mediaBatch.busy) {
          setMediaBatch({ busy: false, msg: `${s.updated || 0} fiche(s) mise(s) à jour.`, done: s.done || 0, total: s.total || 0 });
          await load();
          setTimeout(() => setMediaBatch((m) => ({ ...m, msg: "" })), 8000);
        }
      } catch { /* noop */ }
    };
    if (mediaBatch.busy) timer = setTimeout(poll, 3000);
    return () => timer && clearTimeout(timer);
  }, [mediaBatch.busy]); // eslint-disable-line

  const runMediaBatch = async (onlyMissing) => {
    setMediaBatch({ busy: true, msg: "", done: 0, total: 0 });
    try {
      const r = await adminApi.regenerateMediaBatch({ only_missing: onlyMissing });
      setMediaBatch({ busy: true, msg: "", done: 0, total: r.total || 0 });
    } catch {
      setMediaBatch({ busy: false, msg: "Erreur au lancement.", done: 0, total: 0 });
    }
  };

  const [i18nBatch, setI18nBatch] = useState({ busy: false, msg: "", done: 0, total: 0 });
  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const s = await adminApi.translateAllStatus();
        if (s && s.running) {
          setI18nBatch((b) => ({ ...b, busy: true, msg: "", done: s.done || 0, total: s.total || 0 }));
          timer = setTimeout(poll, 3000);
        } else {
          setI18nBatch((b) => b.busy
            ? { busy: false, msg: `Traduction terminée (${s.listings || 0} annonces × 5 langues).`, done: s.done || 0, total: s.total || 0 }
            : b);
          setTimeout(() => setI18nBatch((m) => ({ ...m, msg: "" })), 8000);
        }
      } catch { /* noop */ }
    };
    if (i18nBatch.busy) timer = setTimeout(poll, 3000);
    return () => timer && clearTimeout(timer);
  }, [i18nBatch.busy]); // eslint-disable-line

  const runTranslateAll = async (force) => {
    setI18nBatch({ busy: true, msg: "", done: 0, total: 0 });
    try {
      await adminApi.translateAll({ force });
    } catch (e) {
      setI18nBatch({ busy: false, msg: e?.message?.includes("cours") ? "Déjà en cours." : "Erreur au lancement.", done: 0, total: 0 });
    }
  };

  const emailOwner = (l) => {
    const link = `${window.location.origin}/bien/${l.slug}`;
    const subject = "Votre annonce OFF MARKETTE";
    const body = `Bonjour,\n\nVotre annonce est déjà disponible sur OFF MARKETTE.\nVous pouvez la consulter ici :\n${link}\n\nMerci encore pour votre confiance. Je reviens vers vous dès que j'aurai une visite.\n\nVincent Marette\nOFF MARKETTE\n+33 6 88 67 53 57`;
    window.location.href = `mailto:${l.owner_email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <AdminShell
      embedded={embedded}
      title="Gestion des annonces"
      action={<Link to="/admin/new" data-testid="admin-new-listing" className="flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-5 py-2.5 transition-colors"><Plus className="w-4 h-4" /> Nouvelle annonce</Link>}
    >
      <GlobalPasswordBar />
      <div className="bg-[#111114] border border-white/10 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="highlights-batch-bar">
        <div className="flex items-center gap-2 text-sm text-zinc-300 shrink-0">
          <Sparkles className="w-4 h-4 text-[#C19A6B]" /> Points forts IA (génération en lot)
        </div>
        <p className="flex-1 text-xs text-zinc-500">
          {missingHlCount > 0 ? `${missingHlCount} annonce(s) sans points forts.` : "Toutes les annonces ont des points forts."} L'IA génère des tags courts pour les fiches concernées.
        </p>
        {hlBatch.msg && <span className="text-xs text-[#C19A6B]" data-testid="highlights-batch-msg">{hlBatch.msg}</span>}
        <button onClick={() => runBatchHighlights(true)} disabled={hlBatch.busy} data-testid="highlights-batch-missing" className="flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors">
          {hlBatch.busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</> : <><Sparkles className="w-4 h-4" /> Générer (sans tags)</>}
        </button>
        <button onClick={() => runBatchHighlights(false)} disabled={hlBatch.busy} data-testid="highlights-batch-all" className="flex items-center justify-center gap-2 border border-white/15 hover:border-[#C19A6B]/50 text-zinc-300 text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors">
          Régénérer tout
        </button>
      </div>
      <div className="bg-[#111114] border border-white/10 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="media-batch-bar">
        <div className="flex items-center gap-2 text-sm text-zinc-300 shrink-0">
          <ImageIcon className="w-4 h-4 text-[#C19A6B]" /> Photos des fiches IA (récupération en lot)
        </div>
        <p className="flex-1 text-xs text-zinc-500">
          Re-récupère les photos des points d'intérêt & la galerie « en images » via la nouvelle source (Wikimedia + Openverse, triées par pertinence).
        </p>
        {mediaBatch.busy && <span className="text-xs text-[#C19A6B] inline-flex items-center gap-1.5" data-testid="media-batch-progress"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {mediaBatch.done}/{mediaBatch.total || "…"}</span>}
        {!mediaBatch.busy && mediaBatch.msg && <span className="text-xs text-[#C19A6B]" data-testid="media-batch-msg">{mediaBatch.msg}</span>}
        <button onClick={() => runMediaBatch(true)} disabled={mediaBatch.busy} data-testid="media-batch-missing" className="flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors">
          {mediaBatch.busy ? <><Loader2 className="w-4 h-4 animate-spin" /> En cours…</> : <><ImageIcon className="w-4 h-4" /> Photos manquantes</>}
        </button>
        <button onClick={() => runMediaBatch(false)} disabled={mediaBatch.busy} data-testid="media-batch-all" className="flex items-center justify-center gap-2 border border-white/15 hover:border-[#C19A6B]/50 text-zinc-300 text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Toutes les photos
        </button>
      </div>
      <div className="bg-[#111114] border border-white/10 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="i18n-batch-bar">
        <div className="flex items-center gap-2 text-sm text-zinc-300 shrink-0">
          <Languages className="w-4 h-4 text-[#C19A6B]" /> Traduction des annonces (6 langues)
        </div>
        <p className="flex-1 text-xs text-zinc-500">
          Pré-traduit le titre, le sous-titre et la description de <strong>toutes les annonces</strong> en EN, ES, DE, PT et 中文 via l'IA (traduit une seule fois, puis mis en cache). Les corrections manuelles verrouillées sont préservées.
        </p>
        {i18nBatch.busy && <span className="text-xs text-[#C19A6B] inline-flex items-center gap-1.5" data-testid="i18n-batch-progress"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {i18nBatch.done}/{i18nBatch.total || "…"}</span>}
        {!i18nBatch.busy && i18nBatch.msg && <span className="text-xs text-[#C19A6B]" data-testid="i18n-batch-msg">{i18nBatch.msg}</span>}
        <button onClick={() => runTranslateAll(false)} disabled={i18nBatch.busy} data-testid="i18n-batch-all" className="flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors">
          {i18nBatch.busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Traduction…</> : <><Languages className="w-4 h-4" /> Traduire tout (6 langues)</>}
        </button>
        <button onClick={() => runTranslateAll(true)} disabled={i18nBatch.busy} data-testid="i18n-batch-force" className="flex items-center justify-center gap-2 border border-white/15 hover:border-[#C19A6B]/50 text-zinc-300 text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Forcer (retraduire)
        </button>
      </div>
      {aiCount > 0 && (
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => setAiFilter((v) => !v)} data-testid="admin-ai-filter" className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${aiFilter ? "bg-[#C19A6B] text-white border-[#C19A6B]" : "border-white/10 text-zinc-300 hover:border-[#C19A6B]/40"}`}>
            <Sparkles className="w-3.5 h-3.5" /> Fiches IA ({aiCount})
          </button>
          {aiFilter && <button onClick={() => setAiFilter(false)} className="text-xs text-zinc-400 hover:text-white underline">Tout afficher</button>}
        </div>
      )}
      {items === null ? (
        <p className="text-zinc-500 text-sm">Chargement…</p>
      ) : shown.length === 0 ? (
        <p className="text-zinc-500 text-sm" data-testid="admin-empty">{aiFilter ? "Aucune fiche IA pour le moment." : "Aucune annonce. Créez votre première annonce."}</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="admin-listings">
          {shown.map((l) => (
            <div key={l.slug} data-testid={`admin-row-${l.slug}`} className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors">
              {l.cover ? (
                <img src={mediaUrl(l.cover)} alt="" className="w-20 h-14 object-cover bg-white/5 shrink-0" />
              ) : (
                <div className="w-20 h-14 bg-white/5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium truncate">{l.title}</h3>
                  {l.featured && <Star className="w-3.5 h-3.5 text-[#C19A6B] fill-[#C19A6B] shrink-0" />}
                  {l.is_flagship && <span className="text-[9px] uppercase tracking-wider bg-[#C19A6B]/20 text-[#C19A6B] px-1.5 py-0.5">Flagship</span>}
                </div>
                <p className="text-xs text-zinc-500 truncate">{l.city} · {l.type} · {l.collection}</p>
              </div>
              <select
                value={l.owner_id || ""}
                onChange={(e) => reassignOwner(l, e.target.value)}
                data-testid={`admin-owner-${l.slug}`}
                title="Conseiller propriétaire"
                className="hidden xl:block bg-white/5 border border-white/10 text-xs text-zinc-300 px-2 py-1.5 max-w-[140px] focus:outline-none focus:border-[#C19A6B]"
              >
                <option value="" className="bg-[#0d0d10]">— Conseiller —</option>
                {members.map((m) => <option key={m.member_id} value={m.member_id} className="bg-[#0d0d10]">{m.display_name}</option>)}
              </select>
              <span className="hidden lg:flex items-center gap-3 text-[11px] text-zinc-400 shrink-0" data-testid={`admin-stats-${l.slug}`}>
                <span className="flex items-center gap-1" title="Vues"><Eye className="w-3.5 h-3.5" /> {l.views || 0}</span>
                <span className="flex items-center gap-1" title="Favoris"><Heart className="w-3.5 h-3.5" /> {l.favorites_count || 0}</span>
              </span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${STATUS_COLORS[l.status] || "bg-white/10 text-white"}`}>{l.status}</span>
              <span data-testid={`admin-visibility-${l.slug}`} className={`text-[10px] uppercase tracking-wider px-2 py-1 shrink-0 ${l.published === false ? "bg-zinc-500/20 text-zinc-400" : "bg-[#C19A6B]/20 text-[#C19A6B]"}`}>{l.published === false ? "Hors ligne" : "En ligne"}</span>
              {(() => {
                const vm = l.visibility_mode || "national";
                const meta = vm === "private"
                  ? { label: "Personnel", cls: "bg-zinc-700/40 text-zinc-200", Icon: EyeOff }
                  : vm === "network"
                    ? { label: l.network_also_national ? "Réseau + Nat." : "Réseau", cls: "bg-[#3b4a63]/40 text-sky-200", Icon: Users }
                    : { label: "National", cls: "bg-sky-700/30 text-sky-200", Icon: Globe };
                const Ic = meta.Icon;
                return (
                  <span data-testid={`admin-vismode-${l.slug}`} title={`Visibilité : ${meta.label}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 shrink-0 ${meta.cls}`}>
                    <Ic className="w-3 h-3" /> {meta.label}
                  </span>
                );
              })()}
              {l.ai_content && l.ai_content.intro && (
                <span data-testid={`admin-ai-badge-${l.slug}`} onClick={() => setAiFilter(true)} title="Filtrer les fiches IA" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 shrink-0 bg-[#C19A6B] text-white font-medium cursor-pointer hover:bg-[#d1af84]"><Sparkles className="w-3 h-3" /> IA</span>
              )}
              <span className="text-sm text-[#C19A6B] w-28 text-right shrink-0">{fmtPrice(l.price)}</span>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => togglePublish(l)} data-testid={`admin-toggle-publish-${l.slug}`} title={l.published === false ? "Mettre en ligne" : "Mettre hors ligne"} className="p-2 border border-white/10 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B]/40 transition-colors">
                  {l.published === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <Link to={`/admin/edit/${l.slug}`} data-testid={`admin-edit-${l.slug}`} className="p-2 border border-white/10 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B]/40 transition-colors"><Pencil className="w-4 h-4" /></Link>
                <button onClick={() => duplicate(l)} disabled={dupBusy === l.slug} data-testid={`admin-duplicate-${l.slug}`} title="Dupliquer (copie hors ligne)" className="p-2 border border-white/10 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B]/40 transition-colors disabled:opacity-50"><Copy className="w-4 h-4" /></button>
                <button onClick={() => setConfirm(l.slug)} data-testid={`admin-delete-${l.slug}`} className="p-2 border border-white/10 text-zinc-300 hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 className="w-4 h-4" /></button>
                <a href={`/bien/${l.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-view-${l.slug}`} title="Voir l'annonce" className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/10 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B]/40 transition-colors text-xs uppercase tracking-wider"><ExternalLink className="w-4 h-4" /> Voir</a>
                <button onClick={() => emailOwner(l)} data-testid={`admin-email-${l.slug}`} title="Envoyer au propriétaire" className="p-2 border border-white/10 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B]/40 transition-colors"><Mail className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setConfirm(null)}>
          <div className="bg-[#0d0d10] border border-white/10 p-6 max-w-sm w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm">
            <h3 className="text-lg font-light">Supprimer cette annonce ?</h3>
            <p className="text-sm text-zinc-400">Cette action est irréversible.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirm(null)} className="px-4 py-2 text-sm text-zinc-400 border border-white/10 hover:text-white">Annuler</button>
              <button onClick={() => del(confirm)} data-testid="delete-confirm-btn" className="px-4 py-2 text-sm bg-red-500/90 hover:bg-red-500 text-white">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
